import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../../utils/baseQuery";
import { components } from "../../types/api";

// Tipos generados desde el swagger del backend (yarn generate:types:local).
type Schemas = components["schemas"];
export type CrmMirrorStatus = Schemas["CrmMirrorStatus"];
export type CommercialPipelineView = Schemas["CommercialPipelineView"];
export type CommercialPipelineColumn = Schemas["CommercialPipelineColumn"];
export type CommercialPipelineCard = Schemas["CommercialPipelineCard"];
export type CommercialPipelineKpis = Schemas["CommercialPipelineKpis"];
export type CommercialConversion = Schemas["CommercialConversion"];
export type OwnerScorecard = Schemas["OwnerScorecard"];
export type DisciplineItem = Schemas["DisciplineItem"];
export type ProposalsPipelineView = Schemas["ProposalsPipelineView"];
export type ProposalsPipelineKpis = Schemas["ProposalsPipelineKpis"];
export type ProposalsAnalysis = Schemas["ProposalsAnalysis"];
export type ProposalBoardCard = Schemas["ProposalBoardCard"];
export type ExecutiveSummaryView = Schemas["ExecutiveSummaryView"];
export type DashboardAlert = Schemas["DashboardAlert"];
export type ExecutiveInsight = Schemas["ExecutiveInsight"];
export type PursuitSheet = Schemas["PursuitSheet"];
export type CommercialDashboardSettings =
  Schemas["CommercialDashboardSettings"];
export type DaysStat = Schemas["DaysStat"];
export type AgeBucket = Schemas["AgeBucket"];
export type BusinessLine = Schemas["BusinessLine"];
export type CommercialBusinessUnit = Schemas["CommercialBusinessUnit"];
export type ClientEngagement = Schemas["ClientEngagement"];
export type House = Schemas["House"];

type SyncResponse = Schemas["ApiResponse_CrmMirrorStatus_"];
type CommercialPipelineResponse =
  Schemas["ApiResponse_CommercialPipelineView_"];
type ProposalsPipelineResponse = Schemas["ApiResponse_ProposalsPipelineView_"];
type SummaryResponse = Schemas["ApiResponse_ExecutiveSummaryView_"];
type PursuitSheetResponse = Schemas["ApiResponse_PursuitSheet_"];
type FilterOptionsResponse = Schemas["ApiResponse_DashboardFilterOptions_"];
type SettingsResponse = Schemas["ApiResponse_CommercialDashboardSettings_"];

/** Los filtros globales del dashboard. Todos opcionales. */
export interface DashboardParams {
  businessUnit?: CommercialBusinessUnit;
  /** YYYY-MM-DD. Sin él, el backend usa el 1 de enero del año en curso. */
  from?: string;
  /** YYYY-MM-DD, incluido. Sin él, hoy. */
  to?: string;
  businessLine?: BusinessLine;
  engagement?: ClientEngagement;
  house?: House;
  owner?: string;
}

export const toQueryString = (params: DashboardParams) => {
  const search = new URLSearchParams();
  (Object.keys(params) as (keyof DashboardParams)[]).forEach((key) => {
    const value = params[key];
    if (value) search.set(key, value);
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

/** El pipeline comercial no tiene House ni negocio: no se mandan. */
const commercialParams = (params: DashboardParams): DashboardParams => ({
  ...params,
  house: undefined,
  businessLine: undefined,
});

export const commercialDashboardApi = createApi({
  reducerPath: "commercialDashboardApi",
  baseQuery: baseQueryWithReauth,
  // CrmMirror: sincronizar con Twenty cambia los datos de todas las vistas.
  tagTypes: ["CrmMirror", "Settings"],
  endpoints: (builder) => ({
    // GET /crm-sync — cuándo se copió Twenty por última vez.
    getCrmSyncStatus: builder.query<SyncResponse, void>({
      query: () => "/commercial-dashboard/crm-sync",
      providesTags: ["CrmMirror"],
    }),

    // POST /crm-sync — sincroniza ya, sin esperar al cron de 15 min. Un 409 significa
    // que ya había una en marcha: no es un error para el usuario, la copia se pone al
    // día igual.
    runCrmSync: builder.mutation<SyncResponse, void>({
      query: () => ({ url: "/commercial-dashboard/crm-sync", method: "POST" }),
      invalidatesTags: ["CrmMirror"],
    }),

    getExecutiveSummary: builder.query<SummaryResponse, DashboardParams>({
      query: (params) =>
        `/commercial-dashboard/summary${toQueryString(params)}`,
      providesTags: ["CrmMirror", "Settings"],
    }),

    getCommercialPipeline: builder.query<
      CommercialPipelineResponse,
      DashboardParams
    >({
      query: (params) =>
        `/commercial-dashboard/commercial-pipeline${toQueryString(commercialParams(params))}`,
      providesTags: ["CrmMirror", "Settings"],
    }),

    getProposalsPipeline: builder.query<
      ProposalsPipelineResponse,
      DashboardParams
    >({
      query: (params) =>
        `/commercial-dashboard/proposals-pipeline${toQueryString(params)}`,
      providesTags: ["CrmMirror", "Settings"],
    }),

    getPursuitSheet: builder.query<PursuitSheetResponse, string>({
      query: (id) => `/commercial-dashboard/pursuits/${encodeURIComponent(id)}`,
      providesTags: ["CrmMirror"],
    }),

    getDashboardFilterOptions: builder.query<FilterOptionsResponse, void>({
      query: () => "/commercial-dashboard/filter-options",
      providesTags: ["CrmMirror"],
    }),

    getDashboardSettings: builder.query<SettingsResponse, void>({
      query: () => "/commercial-dashboard/settings",
      providesTags: ["Settings"],
    }),

    // PUT /settings — solo ADMIN. Recalcula todo lo que depende de objetivos y umbrales.
    updateDashboardSettings: builder.mutation<
      SettingsResponse,
      CommercialDashboardSettings
    >({
      query: (body) => ({
        url: "/commercial-dashboard/settings",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Settings"],
    }),
  }),
});

export const {
  useGetCrmSyncStatusQuery,
  useRunCrmSyncMutation,
  useGetExecutiveSummaryQuery,
  useGetCommercialPipelineQuery,
  useGetProposalsPipelineQuery,
  useGetPursuitSheetQuery,
  useGetDashboardFilterOptionsQuery,
  useGetDashboardSettingsQuery,
  useUpdateDashboardSettingsMutation,
} = commercialDashboardApi;
